import { type DocumentData, type DocumentSnapshot, Timestamp, doc } from 'firebase/firestore'
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage'

import { i18n, isSupportedLocale } from '@/i18n'
import { getFirebaseFirestore, getFirebaseStorage } from '@/lib/firebase/app'
import { callable } from '@/lib/firebase/callable'
import { createReasonGuard } from '@/lib/firebase/errors'
import { type Subscription, subscribeDoc } from '@/lib/firebase/subscribe'
import { isPublishingTime } from '@/lib/brand/publishing-times'
import { asOneOf, asRecords, isRecord, toBrandDocMeta } from '@/repositories/setup-docs'
import {
  BELIEF_BASES,
  BRAND_MARKET_SOURCES,
  CORE_MESSAGE_BASES,
  DEFAULT_PHASE_WEIGHTS,
  JOURNEY_PHASES,
} from '@/types/brand'
import type {
  ArtifactMeta,
  Belief,
  BeliefMap,
  Brand,
  BrandAuthor,
  BrandMarket,
  BrandMarketCandidate,
  ContentStrategy,
  JourneyPhase,
  OnboardingStatus,
  PhaseWeights,
  PostingCadence,
  PostingDay,
  SaveBrandPublishingInput,
  SetBrandMarketInput,
  StyleGuide,
  StyleGuidePreference,
  StyleGuideSample,
  StyleGuideTerm,
  UpdateBrandDocumentPayload,
  VisualStyle,
  VisualStyleAsset,
} from '@/types/brand'
import { POSTING_DAYS } from '@/types/brand'
import { BELIEF_QUESTION_KEYS, type BeliefQuestionKey } from '@/types/content-strategy-run'

export interface BrandScope {
  workspaceId: string
  brandId: string
}

function brandDocRef(scope: BrandScope) {
  return doc(getFirebaseFirestore(), 'workspaces', scope.workspaceId, 'brands', scope.brandId)
}

function artifactDocRef(scope: BrandScope, path: string, ...segments: string[]) {
  return doc(brandDocRef(scope), path, ...segments)
}

function toIsoStringOrNull(value: unknown): string | null {
  return value instanceof Timestamp ? value.toDate().toISOString() : null
}

function toIsoDateOrNull(value: unknown): string | null {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : null
}

const ONBOARDING_STATUSES: readonly OnboardingStatus[] = [
  'pending',
  'collecting-inputs',
  'complete',
]

function asOnboardingStatus(value: unknown): OnboardingStatus {
  return typeof value === 'string' && (ONBOARDING_STATUSES as readonly string[]).includes(value)
    ? (value as OnboardingStatus)
    : 'pending'
}

function toMeta(data: DocumentData): ArtifactMeta {
  const meta = (data.meta ?? {}) as DocumentData
  return { updatedAt: toIsoStringOrNull(meta.updatedAt) }
}

function asFiniteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function toBrandMarketCandidate(record: DocumentData): BrandMarketCandidate | undefined {
  if (typeof record.locationCode !== 'number') return undefined
  const countryCode = typeof record.countryCode === 'string' ? record.countryCode : ''
  if (countryCode === '') return undefined
  return {
    locationCode: record.locationCode,
    countryCode,
    estimatedTraffic: asFiniteOrNull(record.estimatedTraffic),
    rankingKeywordCount: asFiniteOrNull(record.rankingKeywordCount),
  }
}

function toBrandMarket(value: unknown): BrandMarket | undefined {
  if (!isRecord(value)) return undefined
  const decidedAt = toIsoDateOrNull(value.decidedAt)
  if (
    typeof value.locationCode !== 'number' ||
    typeof value.languageCode !== 'string' ||
    value.languageCode === '' ||
    decidedAt === null
  ) {
    return undefined
  }
  return {
    locationCode: value.locationCode,
    languageCode: value.languageCode,
    source: asOneOf(value.source, BRAND_MARKET_SOURCES, 'language-default'),
    candidates: asRecords(value.candidates)
      .map(toBrandMarketCandidate)
      .filter((candidate): candidate is BrandMarketCandidate => candidate !== undefined),
    decidedAt,
  }
}

function toBrandAuthor(value: unknown): BrandAuthor | null {
  if (!isRecord(value)) return null
  const text = (field: unknown): string => (typeof field === 'string' ? field : '')
  return {
    name: text(value.name),
    role: text(value.role),
    bio: text(value.bio),
    profileUrl: text(value.profileUrl),
  }
}

function toBrand(snapshot: DocumentSnapshot<DocumentData>): Brand | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  const market = toBrandMarket(data.market)
  const searchSnapshotRunningAt = toIsoDateOrNull(data.searchSnapshotRunningAt)
  return {
    id: snapshot.id,
    name: String(data.name ?? ''),
    ...(typeof data.timeZone === 'string' && data.timeZone ? { timeZone: data.timeZone } : {}),
    ...(isSupportedLocale(data.contentLanguage) ? { contentLanguage: data.contentLanguage } : {}),
    onboardingStatus: asOnboardingStatus(data.onboardingStatus),
    competitorAnalysisComparingAt: toIsoDateOrNull(data.competitorAnalysisComparingAt),
    competitorsConfirmedAt: toIsoDateOrNull(data.competitorsConfirmedAt),
    visualStyleConfirmedAt: toIsoDateOrNull(data.visualStyleConfirmedAt),
    ...(market !== undefined ? { market } : {}),
    ...(searchSnapshotRunningAt !== null ? { searchSnapshotRunningAt } : {}),
    author: toBrandAuthor(data.author),
    trialUrl: typeof data.trialUrl === 'string' && data.trialUrl !== '' ? data.trialUrl : null,
    createdAt: toIsoStringOrNull(data.createdAt),
    updatedAt: toIsoStringOrNull(data.updatedAt),
  }
}

const setBrandMarketCallable = callable<SetBrandMarketInput, { market: DocumentData }>(
  'setBrandMarket',
)

/** Sets the country and language the brand's search is measured in; resolves the stored market, or `undefined` when it echoed a malformed one. */
export async function setBrandMarket(input: SetBrandMarketInput): Promise<BrandMarket | undefined> {
  const raw = await setBrandMarketCallable(input)
  return toBrandMarket(raw.market)
}

export function subscribeBrand(scope: BrandScope): Subscription<Brand> {
  return subscribeDoc(brandDocRef(scope), toBrand)
}

function asPostingDay(value: unknown): PostingDay | undefined {
  return typeof value === 'string' && (POSTING_DAYS as readonly string[]).includes(value)
    ? (value as PostingDay)
    : undefined
}

function toPreferredTimes(value: unknown): Partial<Record<PostingDay, string>> | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const entries = Object.entries(value as DocumentData)
    .map(([day, time]) => [asPostingDay(day), time] as const)
    .filter(
      (entry): entry is readonly [PostingDay, string] =>
        entry[0] !== undefined && isPublishingTime(entry[1]),
    )
  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

function toPostingCadence(value: unknown): PostingCadence | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  if (typeof record.postsPerWeek !== 'number') return undefined
  const preferredDays = Array.isArray(record.preferredDays)
    ? record.preferredDays.map(asPostingDay).filter((day): day is PostingDay => day !== undefined)
    : undefined
  const preferredTimes = toPreferredTimes(record.preferredTimes)
  return {
    postsPerWeek: record.postsPerWeek,
    ...(preferredDays ? { preferredDays } : {}),
    ...(preferredTimes ? { preferredTimes } : {}),
  }
}

const PHASE_WEIGHTS_TOTAL = 100

function isWeight(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function toPhaseWeights(value: unknown): PhaseWeights {
  if (!isRecord(value)) return DEFAULT_PHASE_WEIGHTS
  const { awareness, consideration, decision } = value
  if (!isWeight(awareness) || !isWeight(consideration) || !isWeight(decision))
    return DEFAULT_PHASE_WEIGHTS
  return awareness + consideration + decision === PHASE_WEIGHTS_TOTAL
    ? { awareness, consideration, decision }
    : DEFAULT_PHASE_WEIGHTS
}

function toContentStrategy(snapshot: DocumentSnapshot<DocumentData>): ContentStrategy | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  const postingCadence = toPostingCadence(data.postingCadence)
  return {
    meta: toBrandDocMeta(data.meta),
    ...(postingCadence ? { postingCadence } : {}),
    phaseWeights: toPhaseWeights(data.phaseWeights),
  }
}

function asJourneyPhase(value: unknown): JourneyPhase | undefined {
  return JOURNEY_PHASES.find((phase) => phase === value)
}

function asQuestionKey(value: unknown): BeliefQuestionKey | null {
  return BELIEF_QUESTION_KEYS.find((key) => key === value) ?? null
}

function toBelief(value: unknown): Belief | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  const slug = String(record.slug ?? '')
  const phase = asJourneyPhase(record.phase)
  const claim = String(record.claim ?? '')
  const objection = String(record.objection ?? '')
  if (slug.trim() === '' || !phase || claim.trim() === '' || objection.trim() === '')
    return undefined
  return {
    slug,
    phase,
    name: String(record.name ?? ''),
    claim,
    objection,
    questionKey: asQuestionKey(record.questionKey),
    basis: asOneOf(record.basis, BELIEF_BASES, 'edited'),
  }
}

function toBeliefMap(snapshot: DocumentSnapshot<DocumentData>): BeliefMap | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  const rawBeliefs = Array.isArray(data.beliefs) ? data.beliefs : []
  return {
    meta: toBrandDocMeta(data.meta),
    obviousChoiceBelief: String(data.obviousChoiceBelief ?? ''),
    obviousChoiceBeliefBasis: asOneOf(data.obviousChoiceBeliefBasis, CORE_MESSAGE_BASES, 'edited'),
    obviousChoiceBeliefReplaces: String(data.obviousChoiceBeliefReplaces ?? ''),
    beliefs: rawBeliefs.map(toBelief).filter((belief): belief is Belief => belief !== undefined),
  }
}

export function subscribeBeliefMap(scope: BrandScope): Subscription<BeliefMap> {
  return subscribeDoc(artifactDocRef(scope, 'beliefMap', 'current'), toBeliefMap)
}

export function subscribeContentStrategy(scope: BrandScope): Subscription<ContentStrategy> {
  return subscribeDoc(artifactDocRef(scope, 'contentStrategy', 'current'), toContentStrategy)
}

function toStyleGuideTerm(value: unknown): StyleGuideTerm | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  const term = String(record.term ?? '')
  return term.trim() === '' ? undefined : { term, correctForm: String(record.correctForm ?? '') }
}

function toStyleGuidePreference(value: unknown): StyleGuidePreference | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  const avoid = String(record.avoid ?? '')
  return avoid.trim() === '' ? undefined : { avoid, useInstead: String(record.useInstead ?? '') }
}

function toStyleGuideSample(value: unknown): StyleGuideSample | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const record = value as DocumentData
  const body = String(record.body ?? '')
  if (body.trim() === '') return undefined
  return {
    body,
    sourceUrl: typeof record.sourceUrl === 'string' ? record.sourceUrl : null,
    publishedAt: typeof record.publishedAt === 'string' ? record.publishedAt : null,
  }
}

function mapList<T>(value: unknown, map: (item: unknown) => T | undefined): T[] {
  return Array.isArray(value) ? value.map(map).filter((item): item is T => item !== undefined) : []
}

function toStyleGuide(snapshot: DocumentSnapshot<DocumentData>): StyleGuide | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  return {
    meta: toMeta(data),
    voiceAndPersonality: String(data.voiceAndPersonality ?? ''),
    formalityAndAddress: String(data.formalityAndAddress ?? ''),
    referenceGrammarStandard: String(data.referenceGrammarStandard ?? ''),
    numbersDatesCurrency: String(data.numbersDatesCurrency ?? ''),
    punctuationAndQuotes: String(data.punctuationAndQuotes ?? ''),
    capitalization: String(data.capitalization ?? ''),
    brandAndProductTerms: mapList(data.brandAndProductTerms, toStyleGuideTerm),
    preferredAndAvoidedWords: mapList(data.preferredAndAvoidedWords, toStyleGuidePreference),
    inclusiveLanguage: String(data.inclusiveLanguage ?? ''),
    sampleCopy: mapList(data.sampleCopy, toStyleGuideSample),
  }
}

export function subscribeStyleGuide(scope: BrandScope): Subscription<StyleGuide> {
  return subscribeDoc(artifactDocRef(scope, 'styleGuide', 'current'), toStyleGuide)
}

function toBrandLogo(value: unknown): VisualStyleAsset | null {
  if (typeof value !== 'object' || value === null) return null
  const record = value as DocumentData
  const name = String(record.name ?? '')
  const src = String(record.src ?? '')
  return name === '' && src === '' ? null : { name, src }
}

function toVisualStyle(snapshot: DocumentSnapshot<DocumentData>): VisualStyle | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  return {
    meta: toMeta(data),
    colors: Array.isArray(data.colors) ? data.colors.map((color: unknown) => String(color)) : [],
    fontFamily: typeof data.fontFamily === 'string' ? data.fontFamily : null,
    logo: toBrandLogo(data.logo),
    monogram: toBrandLogo(data.monogram),
  }
}

export function subscribeVisualStyle(scope: BrandScope): Subscription<VisualStyle> {
  return subscribeDoc(artifactDocRef(scope, 'visualStyle', 'current'), toVisualStyle)
}

const BRAND_DOCUMENT_UPDATE_REASONS = ['editor_role_required'] as const

export const isEditorRoleRequiredReason = createReasonGuard(BRAND_DOCUMENT_UPDATE_REASONS)

/** Applies one user-authored edit to a brand document; the echo is discarded, the subscriptions are the read path. */
export const updateBrandDocument = callable<UpdateBrandDocumentPayload, void>('updateBrandDocument')

export class VisualStyleAssetUploadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'VisualStyleAssetUploadError'
  }
}

const VISUAL_STYLE_ASSET_EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/svg+xml': 'svg',
  'image/webp': 'webp',
}

export const MAX_VISUAL_STYLE_ASSET_FILE_SIZE_BYTES = 2 * 1024 * 1024

/** Uploads one brand mark image and returns its Cloud Storage download URL; type and size are rejected client-side before the server re-validates. */
export async function uploadVisualStyleAsset(
  scope: BrandScope,
  file: File,
): Promise<{ src: string }> {
  const extension = VISUAL_STYLE_ASSET_EXTENSION_BY_CONTENT_TYPE[file.type]
  if (!extension) {
    throw new VisualStyleAssetUploadError(i18n.global.t('brand.visualStyle.errors.invalidFileType'))
  }
  if (file.size > MAX_VISUAL_STYLE_ASSET_FILE_SIZE_BYTES) {
    throw new VisualStyleAssetUploadError(i18n.global.t('brand.visualStyle.errors.fileTooLarge'))
  }

  const path = `workspaces/${scope.workspaceId}/brands/${scope.brandId}/visualStyle/uploads/${crypto.randomUUID()}.${extension}`
  const objectRef = storageRef(getFirebaseStorage(), path)
  try {
    await uploadBytes(objectRef, file, { contentType: file.type })
    return { src: await getDownloadURL(objectRef) }
  } catch {
    throw new VisualStyleAssetUploadError(i18n.global.t('brand.visualStyle.errors.uploadFailed'))
  }
}

/** Saves the brand's article author and trial page; the subscription is the read path. */
export const saveBrandPublishing = callable<SaveBrandPublishingInput, Record<string, never>>(
  'saveBrandPublishing',
)
